(ns common.ui.entity
  (:require ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [clojure.spec.alpha :as spec]
            [common.ui.store :as store]
            [common.widgets.menu :refer [menu]]
            [goog.object :as gobject]
            [manage.content-configuration-page.view.input :as view.input]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [reagent.ratom :as ratom]
            [reitit.frontend.easy :as r.easy]
            [uix.core :as uix :refer [defui $]]
            [uix.re-frame :refer [use-subscribe]]))

(spec/def :entity/ref #(satisfies? store/DataDomainEntity %))

(spec/def :entity/label (spec/or :element :react/element
                                 :attribute keyword?))

(spec/def :entity/icon (spec/or :icon-id :string/not-empty
                                :icon-id-sub :reframe/sub-or-event
                                :element :react/element))

(spec/def :entity/context-menu (spec/coll-of (spec/nilable map?)))
(spec/def :entity/on-activate (spec/coll-of :reframe/sub-or-event))
(spec/def :entity/content :react/element)
(spec/def :entity/read-only? boolean?)
(spec/def :entity/on-close fn?)

;; the query param subscription implementation should accept two arguments:
;; - a DataDomainEntity reference
;; - an optional string with the new value (e.g., user renamed entity title)
(spec/def :entity/query-param
  (spec/cat :id :reframe/sub-or-event-id
            :reference :entity/ref
            :new-value (spec/? (spec/nilable string?))))

(spec/def :entity/configuration
  (spec/merge
   (spec/keys :opt [:entity/icon
                    :entity/on-close
                    :entity/context-menu
                    :entity/content
                    :entity/on-activate
                    :entity/query-param
                    :entity/ref
                    :entity/read-only?])
   (spec/or :renderable (spec/and (spec/keys :req [:entity/label])
                                  #(= (first (:entity/label %)) :element))
            :reference (spec/and (spec/keys :req [:entity/ref :entity/label])
                                 #(= (first (:entity/label %)) :attribute)))))

(defn editable-label [{:keys [input-props after-save-action ref label]}]
  [view.input/input-save-on-blur
   (cond->
     (assoc (view.input/props-entity ref label)
            :validation/stages #{:save-blocking :warning}
            :form-field-props {:sx #js {:flexGrow 1}}
            :control-props (merge {:data-component :list-item-label-input
                                   :data-label-type :input}
                                  input-props))
     (some? after-save-action)
     (update :on-save (fn [save]
                        (fn [new-value]
                          (save new-value)
                          (after-save-action new-value)))))])

(rf/reg-sub-raw
  ::title-sub-1
  (fn [_ [_ ref label]]
    (r/reaction
     (if (keyword? label)
       (str @(store/get-value ref label))
       label))))

;; Refactoring documented & advised at
;; https://day8.github.io/re-frame/FAQs/UseASubscriptionInAnEventHandler/#restructure-your-app
(rf/reg-event-fx
  ::title-after-save-action
  [(rf/inject-cofx :sub {:view-cfg (fn [[_ query-param new-value]]
                                     (conj query-param new-value))})]
  (fn [{:keys [view-cfg]} _]
    {:fx [[:dispatch [:entity/view view-cfg]]]}))

(defui title [{:entity/keys [read-only? ref label query-param]}]
  (let [title-child (use-subscribe [::title-sub-1 ref label])]
    (if (and (not read-only?) (keyword? label))
      (r/as-element [editable-label {:input-props {:input-props {:sx (fn [^js theme]
                                                                       (-> theme .-typography (gobject/get "h3")))}
                                                   :data-test-id :entity-title
                                                   :data-component :entity-title
                                                   :data-label-type :input}
                                     :after-save-action (fn [new-value]
                                                          (when (some? query-param)
                                                            (rf/dispatch [::title-after-save-action query-param new-value])))
                                     :ref ref
                                     :label label}])
      ($ Typography {:variant "h3"
                     :display "inline"
                     :data-test-id :entity-title
                     :data-component :entity-title
                     :data-label-type :readonly}
         (r/as-element title-child)))))

(rf/reg-sub-raw
  ::view-sub-1
  (fn [_ [_ icon]]
    (r/reaction
     (cond
       (string? icon) icon
       (vector? icon) @(rf/subscribe icon)
       ;; Because somewhere, icon is set as a React element.
       :else icon))))

(defui view [{:entity/keys [icon context-menu action-bar content read-only? on-close on-activate] :as cfg}]
  {:pre [(spec/assert :entity/configuration cfg)]}

  (uix/use-effect (fn []
                    (doseq [event on-activate]
                      (rf/dispatch event))
                    nil)
                  [on-activate])

  (let [icon-name (use-subscribe [::view-sub-1 icon])]
    ($ Stack {:height "100%"}
       ;; Header
       ($ Stack {:px 3
                 :py 2
                 :spacing 2
                 :backgroundColor "grey.90"}
          ($ Stack {:direction "row"
                    :alignItems "center"
                    :spacing 2}
             ;; back button
             ($ IconButton {:onClick #(.back js/history)
                            :sx #js {:padding 0}}
                ($ Icon "arrow_back"))

             ;; icon and title
             ($ Stack {:sx #js {:flexGrow 1}
                       :direction "row"
                       :alignItems "center"
                       :spacing 1}
                (when (some? icon-name)
                  ($ Icon icon-name))
                ($ title cfg))

             ;; context menu and close
             ($ Stack {:direction "row"
                       :spacing 1}
                (when (and context-menu (not read-only?))
                  ($ menu {:icon "more_vert"
                           :items context-menu}))
                (when on-close
                  ($ IconButton {:onClick on-close
                                 :data-testid :close-entity-button
                                 :sx #js {:padding 0}}
                     ($ Icon "close")))))

          ;; action bar, if provided
          (when action-bar
            (r/as-element action-bar)))

       ;; Content
       ($ Stack
          {:sx #js {:overflowY "hidden"
                    :flexGrow 1}}
          (r/as-element content)))))

;; when implementing this function (defmethod), ensure that this expression is
;; evaluating its subscriptions (if any) in a non-lazy way due to use of
;; ratom/reaction later in the code.
(defmulti query-param->config :entity)

(defmethod query-param->config nil [_] nil)

(defmethod query-param->config :default [qp] (prn :unhandled-query-params qp))

(rf/reg-sub :entity/current-query-params
  :<- [:router/current-route]
  (fn [current-route _]
    (when-let [entity (get-in current-route [:parameters :query :entity])]
      (assoc (:query-params current-route) :entity entity))))

(rf/reg-sub :entity/->query-params
  :<- [:router/query-parameters-pre-coercion]
  (fn [query-params [_ {:keys [menu entity] :as entity-route-params}]]
    (cond-> (merge (dissoc query-params :entity) entity-route-params)
      (keyword? menu) (assoc :menu menu)
      (keyword? entity) (assoc :entity (name entity)))))

(rf/reg-event-fx :entity/view
  [(rf/inject-cofx :sub [:router/handler-name])
   (rf/inject-cofx :sub [:router/path-params])
   (rf/inject-cofx :sub [:router/fragment])
   (rf/inject-cofx :sub (fn [[_ query-params]] [:entity/->query-params query-params]))]
  (fn [{:router/keys [handler-name path-params fragment] query-params :entity/->query-params} _]
    {:fx [[:router/navigate!
           [handler-name
            {:params path-params
             :query query-params
             :fragment fragment}]]]}))

(rf/reg-sub :entity/link
  (fn [[_ query-params]]
    [(rf/subscribe [:router/handler-name])
     (rf/subscribe [:router/path-params])
     (rf/subscribe [:entity/->query-params query-params])
     (rf/subscribe [:router/fragment])])
  (fn [[handler-name path-params query-params fragment] _]
    (r.easy/href handler-name path-params query-params (some-> fragment name))))

(rf/reg-event-fx :entity/close-view
  [(rf/inject-cofx :sub [:router/handler-name])
   (rf/inject-cofx :sub [:router/path-params])
   (rf/inject-cofx :sub [:router/fragment])]
  (fn [{:router/keys [handler-name path-params fragment]} _]
    {:fx [[:router/navigate!
           [handler-name
            {:params path-params
             :query {}
             :fragment fragment}]]]}))

(rf/reg-sub-raw :entity/publish-blocking-errors-and-warnings
  (fn [_ [_ {:validation/keys [pull-pattern schema]
             :entity/keys [ref]
             :or {pull-pattern '[*]}}]]
    (ratom/reaction
     (let [validation-results (if (and
                                   @(rf/subscribe [:flag/enabled :content-function-skip-publish-validation])
                                   @(rf/subscribe [:page.content-configuration/validation-disabled]))
                                []
                                @(rf/subscribe [:validation/results-from-db (:eid ref) schema pull-pattern]))]
       (->> validation-results
            (filter #(contains? #{:publish-blocking :warning} (:stage %)))
            (group-by :stage))))))

(rf/reg-sub-raw
  ;; raw because query-param->config implementations may contain subscriptions.
  ;; if there is any subs in query-param->config implementations, these should be
  ;; non-lazy
  :entity/current
  (fn [_ [_ menus]]
    (ratom/reaction
     (some->
      (or (query-param->config @(rf/subscribe [:entity/current-query-params])) (get menus (keyword @(rf/subscribe [:side-menu/current]))))
      (assoc :entity/on-close #(rf/dispatch [:entity/close-view]))))))
